import { styleConstants } from '@paywithextend/components'
import {
  base_colorAction,
  base_colorAlert,
  base_colorContentBorder,
  base_colorSurfaceMuted,
  cta_linkTextPrimary,
  input_backgroundDefault,
  input_borderDefault,
  tag_colorTagSevereBackground,
  tag_colorTagSevereText,
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { css } from 'styled-components'

// Literal port of prototypes/extend-downgrade DowngradeConfirmModal +
// WarnBanner (severe). Every value mirrors the prototype's text-styles.css
// and inline style values 1:1.
export default css`
  display: flex;
  flex-direction: column;
  gap: 40px;
  width: 100%;

  .CancelSubscriptionConfirm-content {
    display: flex;
    flex-direction: column;
    gap: 40px;
    width: 100%;
  }

  .CancelSubscriptionConfirm-upper {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
  }

  .CancelSubscriptionConfirm-title-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  /* text-heading-h3: Rubik / 20 / 500 / 24 / 0 */
  .CancelSubscriptionConfirm-header {
    font-family: ${styleConstants.fontFamilies.medium};
    font-size: 20px;
    line-height: 24px;
    font-weight: 500;
    letter-spacing: 0;
    color: ${text_colorTextPrimary};
    margin: 0;
  }

  /* text-paragraph-support-text-regular: Rubik / 14 / 400 / 18 / 0 */
  .CancelSubscriptionConfirm-subheader {
    font-family: ${styleConstants.fontFamilies.regular};
    font-size: 14px;
    line-height: 18px;
    font-weight: 400;
    letter-spacing: 0;
    color: ${text_colorTextPrimary};
    margin: 0;
  }

  /* WarnBanner severe — 1px severe-text border, severe-bg, severe-text. */
  .CancelSubscriptionConfirm-banner {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 8px;
    background: ${tag_colorTagSevereBackground};
    border: 1px solid ${tag_colorTagSevereText};
    color: ${tag_colorTagSevereText};
  }

  .CancelSubscriptionConfirm-banner-icon {
    flex-shrink: 0;
    margin-top: 1px;
  }

  /* Triangle interior fills with the severe-bg token (not currentColor)
     so it reads as a light pink against the severe-text stroke. */
  .CancelSubscriptionConfirm-banner-icon-triangle {
    fill: ${tag_colorTagSevereBackground};
  }

  /* Features card — padding 20, radius 8, gap 20, 1px grey450 border
     (= --color-base-content-border #DADBE0), grey150 background. */
  .CancelSubscriptionConfirm-features {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 20px;
    border-radius: 8px;
    border: 1px solid ${base_colorContentBorder};
    background: ${base_colorSurfaceMuted};
  }

  .CancelSubscriptionConfirm-features-inner {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  /* text-heading-h4: 16 / 500 / 20 / 0 */
  .CancelSubscriptionConfirm-features-header {
    font-family: ${styleConstants.fontFamilies.medium};
    font-size: 16px;
    line-height: 20px;
    font-weight: 500;
    letter-spacing: 0;
    color: ${text_colorTextPrimary};
    margin: 0;
  }

  .CancelSubscriptionConfirm-features-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .CancelSubscriptionConfirm-feature-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .CancelSubscriptionConfirm-feature-icon {
    flex-shrink: 0;
    display: inline-flex;
    color: ${cta_linkTextPrimary};
  }

  .CancelSubscriptionConfirm-feature-icon svg {
    width: 16px;
    height: 16px;
  }

  /* text-paragraph-support-text-regular: 14 / 400 / 18 / 0 */
  .CancelSubscriptionConfirm-feature-text {
    flex: 1;
    margin: 0;
    font-family: ${styleConstants.fontFamilies.regular};
    font-size: 14px;
    line-height: 18px;
    font-weight: 400;
    letter-spacing: 0;
    color: ${text_colorTextPrimary};
  }

  /* Inline-button styled link — Rubik / 14 / 500 / 18 / 0, CTA blue. */
  .CancelSubscriptionConfirm-view-all {
    align-self: flex-start;
    font-family: ${styleConstants.fontFamilies.medium};
    font-size: 14px;
    line-height: 18px;
    font-weight: 500;
    letter-spacing: 0;
    color: ${base_colorAction};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  .CancelSubscriptionConfirm-confirm-row {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  /* text-heading-h4: 16 / 500 / 20 / 0 */
  .CancelSubscriptionConfirm-confirm-label {
    font-family: ${styleConstants.fontFamilies.medium};
    font-size: 16px;
    line-height: 20px;
    font-weight: 500;
    letter-spacing: 0;
    color: ${text_colorTextPrimary};
    margin: 0;
  }

  .CancelSubscriptionConfirm-confirm-phrase {
    color: ${base_colorAlert};
  }

  /* Type-to-confirm input — 50px tall, padding 0 20, radius 8, grey400
     border, grey100 background, 14/18 body text. */
  .CancelSubscriptionConfirm-input {
    width: 100%;
    height: 50px;
    padding: 0 20px;
    border-radius: 8px;
    border: 1px solid ${input_borderDefault};
    background: ${input_backgroundDefault};
    font-family: ${styleConstants.fontFamilies.regular};
    font-size: 14px;
    line-height: 18px;
    color: ${text_colorTextPrimary};
    box-sizing: border-box;
    outline: none;
  }

  /* Restore a keyboard-visible focus indicator (the bare outline: none
     above strips the native ring). Uses :focus-visible so mouse focus
     stays unstyled but keyboard tab/Enter shows a clear blue ring. */
  .CancelSubscriptionConfirm-input:focus-visible {
    outline: none;
    border-color: ${base_colorAction};
    box-shadow: 0 0 0 3px ${base_colorAction}33;
  }

  .CancelSubscriptionConfirm-buttons {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    /* Cancel = ext-ui variant="no-border" — plain link, secondary color. */
    .Button.no-border {
      color: ${text_colorTextSecondary};

      & g {
        fill: ${text_colorTextSecondary};
      }

      &:hover {
        color: ${text_colorTextSecondary};
        background-color: transparent;
      }
    }
  }
`
